import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiExternalLink, FiGithub } from 'react-icons/fi';
import { getAssetUrl } from '../../utils/api';

const ProjectCard = ({ project }) => {
  const imageUrl = project.image ? getAssetUrl(project.image) : '/placeholder-project.svg';

  return (
    <CardContainer
      whileHover={{ y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <ImageContainer>
        <img src={imageUrl} alt={project.title} />
        <Overlay>
          <OverlayLinks>
            {project.demoLink && (
              <OverlayLink href={project.demoLink} target="_blank" rel="noopener noreferrer">
                <FiExternalLink /> Live Demo
              </OverlayLink>
            )}
            {project.githubLink && (
              <OverlayLink href={project.githubLink} target="_blank" rel="noopener noreferrer">
                <FiGithub /> GitHub
              </OverlayLink>
            )}
          </OverlayLinks>
        </Overlay>
      </ImageContainer>
      <CardContent>
        <CardTitle>
          <Link to={`/projects/${project.id}`}>{project.title}</Link>
        </CardTitle>
        <CardDescription>{project.description}</CardDescription>
        <TagsContainer>
          {project.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </CardContent>
    </CardContainer>
  );
};

const CardContainer = styled(motion.div)`
  border-radius: var(--border-radius);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: -100%; width: 50%; height: 100%;
    background: linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent);
    transform: skewX(-20deg);
    transition: 0.6s;
    pointer-events: none;
    z-index: 2;
  }
  
  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  .light &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 255, 255, 0.8);
  }
  
  &:hover::before {
    left: 200%;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--glass-border);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  
  &:hover {
    img {
      transform: scale(1.08) translateY(-5px);
    }
    
    > div {
      opacity: 1;
    }
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const OverlayLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const OverlayLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--primary-light);
  color: white;
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--secondary-light);
    transform: translateY(-2px);
  }
  
  .dark & {
    background-color: var(--primary-dark);
    
    &:hover {
      background-color: var(--secondary-dark);
    }
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  
  a {
    .light & {
      color: var(--text-primary-light);
    }
    
    .dark & {
      color: var(--text-primary-dark);
    }
    
    &:hover {
      .light & {
        color: var(--primary-light);
      }
      
      .dark & {
        color: var(--primary-dark);
      }
    }
  }
`;

const CardDescription = styled.p`
  margin-bottom: 1.5rem;
  flex: 1;
  
  .light & {
    color: var(--text-secondary-light);
  }
  
  .dark & {
    color: var(--text-secondary-dark);
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  
  .light & {
    background-color: var(--bg-secondary-light);
    color: var(--primary-light);
  }
  
  .dark & {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--primary-dark);
  }
`;

export default ProjectCard;